import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { Spacer } from '@chakra-ui/react';

import { Icon, Heading, Paragraph } from 'components';
import colors from 'styles/themes/wikifactory/styles/colors';
import { staticURL } from 'core/url';
import Tooltip from 'components/Tooltip';
import Spinner from 'components/Spinner';
import useFileInput from 'libs/useFileInput';

import downloadFromUrl from 'utils/downloadFromUrl';

import {
  FileName,
  InlineButton,
  UploadRowsWrapper,
  UploadRow,
  InfoIcon,
} from './supportingFilesSection.styled';

const SupportingFilesSection = observer(
  ({
    supportingFiles,
    supportingFilesRequirements,
    supportingFilesBeingAdded,
    onAddSupportingFiles,
    onRemoveSupportingFile,
    shouldShowTooltip,
    canUpdateFiles,
    areFileRequirementsValidatedByServer,
  }) => {
    const selectFiles = useFileInput(onAddSupportingFiles);

    const uploadedFileRows = supportingFiles
      ?.slice()
      .sort(
        (fileA, fileB) =>
          new Date(fileB.fileLastModified).getTime() -
          new Date(fileA.fileLastModified).getTime(),
      )
      .map(supportingFile => {
        const onRemove = () => {
          onRemoveSupportingFile(supportingFile);
        };

        const onDownload = () => {
          downloadFromUrl(supportingFile.permalink);
        };

        return (
          <UploadedSupportingFileRow
            supportingFile={supportingFile}
            key={supportingFile.id}
            onRemove={onRemove}
            onDownload={onDownload}
            canBeRemoved={canUpdateFiles}
          />
        );
      });

    const fileBeingAddedRows = supportingFilesBeingAdded.map((file, index) => {
      return <FileBeingAddedRow key={file.name + index} filename={file.name} />;
    });

    const fileFormatsBeingAdded = {};
    supportingFilesBeingAdded.forEach(file => {
      const index = file.name.lastIndexOf('.');
      fileFormatsBeingAdded[file.name.substring(index)] = true;
    });

    let supportingFilesRequirementsToDisplay = supportingFilesRequirements.filter(
      fileRequirement => {
        for (let i = 0; i < fileRequirement.formats.length; i += 1) {
          if (fileFormatsBeingAdded[fileRequirement.formats[i]]) return false;
        }
        return true;
      },
    );

    if (!areFileRequirementsValidatedByServer) {
      /*
        NOTE (david): This whole block is to address visual flicker in the UI in a common case. 
        This is the UI trying to figure out what requirements are being fullfilled until it has
        server confirmation. Ideally this should not be necessary if there were a more tightly
        defined relationship between suppFiles and the requirements they are potentially fullfilling.
        Because the data from the server is not that rich we are going to look at file extensions
        and presume requirements are being fullfilled. This breaks in some cases with multiple 
        requirements for the same file type, but that's an edge case much narrower than the common 
        case this addresses: After adding a supp file in rfqForm there's a bit of time where the
        file has been added locally successfully but we are fetching requirements, once the server 
        confirms that that file fullfills a requirement then the requirement visualy disappears - that
        causes a flicker where there's a UI element that appears/disappears in that latency.
      */
      const supportingFilesAvailable = [...supportingFiles];
      supportingFilesRequirementsToDisplay = supportingFilesRequirementsToDisplay.filter(
        fileRequirement => {
          for (let i = 0; i < fileRequirement.formats.length; i += 1) {
            const extension = fileRequirement.formats[i];
            for (let j = 0; j < supportingFilesAvailable.length; j += 1) {
              const file = supportingFilesAvailable[j];
              if (file.filename.endsWith(extension)) {
                supportingFilesAvailable.splice(j, 1);
                return false;
              }
            }
          }
          return true;
        },
      );
    }

    const requiredFileRows = supportingFilesRequirementsToDisplay.map(
      fileRequirement => {
        return (
          <RequiredFileRow
            key={fileRequirement.id}
            formats={fileRequirement.formats}
            requirementFor={fileRequirement.for.name}
            fileRequirement={fileRequirement}
            onAddSupportingFiles={onAddSupportingFiles}
          />
        );
      },
    );

    const isThereNoContentBeingRendered =
      uploadedFileRows.length === 0 &&
      fileBeingAddedRows.length === 0 &&
      requiredFileRows.length === 0 &&
      !canUpdateFiles;

    return (
      <>
        <Heading level='2' size='large' margin='0 0 0.75rem 0'>
          <Trans>Supporting Files</Trans>
          {shouldShowTooltip && (
            <>
              <Tooltip
                delayShow={200}
                place='bottom'
                id='supportingFilesSectionTooltip'
              />
              <InfoIcon
                name='info'
                size='default'
                data-for='supportingFilesSectionTooltip'
                data-tip='Supporting files might be required by some of the manufacturers in order to provide a quote.'
              />
            </>
          )}
        </Heading>
        <UploadRowsWrapper>
          {uploadedFileRows}
          {fileBeingAddedRows}
          {requiredFileRows}
          {canUpdateFiles && (
            <UploadNewSupportingFileRow onUploadClick={selectFiles} />
          )}
          {isThereNoContentBeingRendered && (
            <Paragraph>
              <Trans>There were no supporting files uploaded.</Trans>
            </Paragraph>
          )}
        </UploadRowsWrapper>
      </>
    );
  },
);

SupportingFilesSection.propTypes = {
  supportingFiles: PropTypes.array,
  supportingFilesBeingAdded: PropTypes.array,
  areFileRequirementsValidatedByServer: PropTypes.bool,
  manager: PropTypes.object,
  onAddSupportingFiles: PropTypes.func,
  onRemoveSupportingFile: PropTypes.func,
  shouldShowTooltip: PropTypes.bool,
  canUpdateFiles: PropTypes.bool,
  supportingFilesRequirements: PropTypes.array,
};

SupportingFilesSection.defaultProps = {
  supportingFiles: [],
  supportingFilesRequirements: [],
  areFileRequirementsValidatedByServer: true,
  supportingFilesBeingAdded: [],
  shouldShowTooltip: true,
  canUpdateFiles: true,
};

const UploadedSupportingFileRow = observer(props => {
  const { supportingFile, onRemove, onDownload, canBeRemoved } = props;
  return (
    <UploadRow>
      <img src={staticURL('assets/icons/fileArtifact.svg')} />
      <FileName>{supportingFile.filename}</FileName>
      <Icon
        name='success'
        size='default'
        viewBox='0 0 30 30'
        colorPath={colors.wfGreen}
      />
      <Spacer />
      <InlineDownloadButton onClick={onDownload} />
      {canBeRemoved && (
        <InlineRemoveButton
          onClick={onRemove}
          loading={supportingFile.isBeingRemoved}
        />
      )}
    </UploadRow>
  );
});

UploadedSupportingFileRow.propTypes = {
  supportingFile: PropTypes.object,
  onRemove: PropTypes.func,
  onDownload: PropTypes.func,
  canBeRemoved: PropTypes.bool,
};

const FileBeingAddedRow = ({ filename }) => {
  return (
    <UploadRow>
      <img src={staticURL('assets/icons/fileArtifact.svg')} />
      <FileName>{filename}</FileName>
      <Spinner size='small' />
      <Spacer />
    </UploadRow>
  );
};

FileBeingAddedRow.propTypes = {
  filename: PropTypes.string,
};

const RequiredFileRow = ({ onAddSupportingFiles, formats, requirementFor }) => {
  const formatsText = formats.map(format => `*${format}`).join(', ');
  const selectFiles = useFileInput(onAddSupportingFiles, {
    accept: formats.join(','),
  });

  return (
    <UploadRow>
      <img src={staticURL('assets/icons/requiredFile.svg')} />
      <FileName>
        {formatsText} (Required by value of {requirementFor})
      </FileName>
      <Spacer />
      <InlineUploadButton onClick={selectFiles} />
    </UploadRow>
  );
};

RequiredFileRow.propTypes = {
  onAddSupportingFiles: PropTypes.func,
  formats: PropTypes.array,
  requirementFor: PropTypes.string,
};

const UploadNewSupportingFileRow = ({ onUploadClick }) => {
  return (
    <UploadRow>
      <img src={staticURL('assets/icons/fileToUpload.svg')} />
      <FileName variant='tertiary'>Any file type (Optional)</FileName>
      <Spacer />
      <InlineUploadButton onClick={onUploadClick} />
    </UploadRow>
  );
};

UploadNewSupportingFileRow.propTypes = {
  onUploadClick: PropTypes.func,
};

const InlineUploadButton = ({ onClick }) => (
  <InlineButton
    text={<Trans>Upload</Trans>}
    onClick={onClick}
    icon='upload'
    size='small'
    iconPosition='left'
    appearance='plain'
    removeBold
  />
);

InlineUploadButton.propTypes = {
  onClick: PropTypes.func,
};

const InlineDownloadButton = ({ onClick }) => (
  <InlineButton
    icon='download_local'
    text={<Trans>Download</Trans>}
    onClick={onClick}
    size='small'
    iconViewBox='0 0 16 16'
    iconPosition='left'
    appearance='plain'
    removeBold
  />
);
InlineDownloadButton.propTypes = {
  onClick: PropTypes.func,
};

const InlineRemoveButton = ({ onClick, loading }) => (
  <InlineButton
    text={<Trans>Remove</Trans>}
    onClick={onClick}
    icon='close_small'
    size='small'
    iconPosition='left'
    appearance='plain'
    variant='secondary'
    removeBold
    loading={loading}
  />
);
InlineRemoveButton.propTypes = {
  onClick: PropTypes.func,
  loading: PropTypes.bool,
};
InlineRemoveButton.defaultProps = {
  loading: false,
};

export default withNamespaces()(SupportingFilesSection);
